import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, FormControl, FormGroup, ControlLabel } from 'react-bootstrap';
import Utils from '../../util/Utils.jsx';
import { useStateValue } from '../../context/state.jsx';
import { openAlert } from '../../context/alert.js';

/**
 * Multi-stage project destruction modal with reassign / force-delete paths.
 *
 * @param {object} props
 * @param {boolean} props.show
 * @param {{ id: number, name: string }|null} props.project
 * @param {Function} props.onClose
 * @param {Function} props.onDestroyed
 */
const ProjectDestroyModal = ({ show, project, onClose, onDestroyed }) => {
	const [, dispatch] = useStateValue();
	const [stage, setStage] = useState(1);
	const [preview, setPreview] = useState(null);
	const [newOwnerId, setNewOwnerId] = useState('');
	const [confirmName, setConfirmName] = useState('');
	const [busy, setBusy] = useState(false);

	useEffect(() => {
		if (!show || !project) {
			setStage(1);
			setPreview(null);
			setNewOwnerId('');
			setConfirmName('');
			return;
		}
		let cancelled = false;
		(async () => {
			setBusy(true);
			try {
				const result = await Utils.ajaxAsync('adminProjectDestroy', {
					action: 'preview',
					projectId: project.id
				}, dispatch, { suppressErrorAlert: true });
				if (!cancelled && result.success) {
					setPreview(result);
					setStage(result.isShared ? 2 : 3);
				} else if (!cancelled) {
					dispatch(openAlert('Error', result.error || 'Could not load project dependencies.'));
					onClose();
				}
			} catch (e) {
				if (!cancelled) {
					dispatch(openAlert('Error', e.message || 'Preview failed.'));
					onClose();
				}
			} finally {
				if (!cancelled) setBusy(false);
			}
		})();
		return () => { cancelled = true; };
	}, [show, project]);

	const runForceDelete = async ({ reassignFirst }) => {
		if (!project) return;
		if (confirmName !== project.name) {
			dispatch(openAlert('Confirmation required', 'Type the exact project name to continue.'));
			return;
		}
		setBusy(true);
		try {
			const payload = {
				action: reassignFirst ? 'reassignAndDelete' : 'forceDelete',
				projectId: project.id,
				confirmName
			};
			if (reassignFirst) {
				if (!newOwnerId) {
					dispatch(openAlert('Re-assign required', 'Select a new owner before continuing.'));
					setBusy(false);
					return;
				}
				payload.newOwnerId = newOwnerId;
			}
			const result = await Utils.ajaxAsync('adminProjectDestroy', payload, dispatch, { suppressErrorAlert: true });
			if (result.success) {
				dispatch(openAlert('Project destroyed', `"${project.name}" has been permanently deleted.`));
				onDestroyed();
				onClose();
			} else {
				dispatch(openAlert('Error', result.error || 'Destruction failed.'));
			}
		} catch (e) {
			dispatch(openAlert('Error', e.message || 'Destruction failed.'));
		} finally {
			setBusy(false);
		}
	};

	const runReassignOnly = async () => {
		if (!newOwnerId) {
			dispatch(openAlert('Re-assign required', 'Select a new owner.'));
			return;
		}
		setBusy(true);
		try {
			const result = await Utils.ajaxAsync('adminProjectDestroy', {
				action: 'reassign',
				projectId: project.id,
				newOwnerId
			}, dispatch, { suppressErrorAlert: true });
			if (result.success) {
				dispatch(openAlert('Ownership transferred', `New owner: ${result.newOwnerEmail}`));
				setStage(3);
			} else {
				dispatch(openAlert('Error', result.error || 'Reassign failed.'));
			}
		} catch (e) {
			dispatch(openAlert('Error', e.message || 'Reassign failed.'));
		} finally {
			setBusy(false);
		}
	};

	if (!project) return null;

	const members = (preview && preview.members) || [];

	return (
		<Modal show={show} onHide={onClose} backdrop="static">
			<Modal.Header closeButton>
				<Modal.Title>Destroy project — stage {stage}</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				{busy && !preview && <p>Checking dependencies…</p>}

				{stage === 2 && preview && (
					<div className="superAdminDestroyShared">
						<p>
							<strong>{project.name}</strong> is shared with{' '}
							<strong>{preview.memberCount}</strong> user(s). Deleting without
							re-assignment may leave orphaned permission history and disrupt those accounts.
						</p>
						<ul className="superAdminMemberList">
							{members.map(m => (
								<li key={m.id}>{m.emailAddress}{m.name ? ` (${m.name})` : ''}</li>
							))}
						</ul>
						<FormGroup>
							<ControlLabel>Re-assign ownership to</ControlLabel>
							<FormControl
								componentClass="select"
								value={newOwnerId}
								onChange={e => setNewOwnerId(e.target.value)}>
								<option value="">Select user…</option>
								{members.map(m => (
									<option key={m.id} value={m.id}>{m.emailAddress}</option>
								))}
							</FormControl>
						</FormGroup>
						<div className="superAdminDestroyActions">
							<Button bsStyle="primary" disabled={busy} onClick={runReassignOnly}>
								Re-assign ownership
							</Button>
							<Button
								bsStyle="danger"
								disabled={busy}
								onClick={() => setStage(3)}>
								Force Delete (with warning)
							</Button>
						</div>
					</div>
				)}

				{stage === 3 && (
					<div className="superAdminDestroyConfirm">
						<p className="superAdminDangerBanner">
							This permanently deletes the project, all SET_* tables, layouts, and dataset files.
							This cannot be undone.
						</p>
						{preview && preview.isShared && !newOwnerId && (
							<p>
								You chose <strong>Force Delete</strong> while the project is still shared.
								Shared users will lose access immediately.
							</p>
						)}
						{preview && preview.isShared && newOwnerId && (
							<p>
								After confirmation the project will be re-assigned to the selected owner, then destroyed.
							</p>
						)}
						<FormGroup>
							<ControlLabel>Type the project name to confirm: <code>{project.name}</code></ControlLabel>
							<FormControl
								type="text"
								value={confirmName}
								onChange={e => setConfirmName(e.target.value)}
								placeholder={project.name}
							/>
						</FormGroup>
					</div>
				)}
			</Modal.Body>
			<Modal.Footer>
				<Button onClick={onClose} disabled={busy}>Cancel</Button>
				{stage === 3 && (
					<Button
						bsStyle="danger"
						disabled={busy || confirmName !== project.name}
						onClick={() => runForceDelete({
							reassignFirst: Boolean(preview && preview.isShared && newOwnerId)
						})}>
						Permanently destroy project
					</Button>
				)}
			</Modal.Footer>
		</Modal>
	);
};

ProjectDestroyModal.propTypes = {
	show: PropTypes.bool.isRequired,
	project: PropTypes.shape({
		id: PropTypes.number,
		name: PropTypes.string
	}),
	onClose: PropTypes.func.isRequired,
	onDestroyed: PropTypes.func.isRequired
};

export default ProjectDestroyModal;
